import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { listPublicVideos } from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { Radio, Play, Youtube, ExternalLink } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { openExternalUrl } from '@/utils/nativeAndroidFiles';

export default function Transmissao() {
  const { data: churchInfo, isLoading } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const info = await base44.entities.ChurchInfo.list();
      return info[0] || null;
    }
  });

  const { data: recentVideos = [] } = useQuery({
    queryKey: ['recentVideos'],
    queryFn: () => listPublicVideos(4)
  });

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|channel\/|c\/|user\/|@)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeChannelId = (url) => {
    const match = String(url || '').match(/youtube\.com\/channel\/([^/?&#]+)/i);
    return match?.[1] || null;
  };

  const getEmbedUrl = (url) => {
    const rawUrl = String(url || '').trim();
    if (!rawUrl) return null;
    if (rawUrl.includes('/embed/')) return rawUrl;

    const videoId = getYouTubeId(rawUrl);
    if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

    const channelId = getYouTubeChannelId(rawUrl);
    if (channelId) return `https://www.youtube.com/embed/live_stream?channel=${channelId}&autoplay=1`;

    return null;
  };

  const liveUrl =
    churchInfo?.youtube_live_url ||
    churchInfo?.live_stream_url ||
    churchInfo?.transmission_url ||
    churchInfo?.youtube_channel ||
    '';
  const liveEmbedUrl = getEmbedUrl(liveUrl);
  const liveExternalUrl = liveUrl || churchInfo?.youtube_channel || '';

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  const getYouTubeFeedUrl = (value) => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return null;
    if (rawValue.includes('feeds/videos.xml')) return rawValue;

    const channelId = getYouTubeChannelId(rawValue);
    if (channelId) return `https://www.youtube.com/feeds/videos.xml?channel_id=${channelId}`;

    return null;
  };

  const youtubeFeedUrl = getYouTubeFeedUrl(churchInfo?.youtube_videos_feed_url || churchInfo?.youtube_channel);

  const { data: youtubeVideos = [] } = useQuery({
    queryKey: ['youtubeRecentVideos', youtubeFeedUrl],
    enabled: !!youtubeFeedUrl,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const rssUrl = encodeURIComponent(youtubeFeedUrl);
      const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${rssUrl}`);
      if (!response.ok) throw new Error('Não foi possível buscar vídeos do YouTube.');
      const data = await response.json();
      if (data.status !== 'ok') throw new Error(data.message || 'Feed do YouTube indisponível.');

      return (data.items || []).slice(0, 4).map((item) => {
        const videoId = getYouTubeId(item.link || item.guid || '');
        return {
          id: videoId || item.guid || item.link,
          title: item.title,
          youtube_url: item.link,
          thumbnail_url: videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : item.thumbnail,
        };
      });
    }
  });

  const displayVideos = [
    ...new Map(
      [...youtubeVideos, ...recentVideos]
        .filter((video) => video?.youtube_url)
        .map((video) => [getYouTubeId(video.youtube_url) || video.youtube_url, video])
    ).values()
  ].slice(0, 4);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-24">
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Transmissão" subtitle="Ao vivo e sob demanda" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Live Stream Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-2xl overflow-hidden mb-6"
        >
          <div className="aspect-video bg-black flex flex-col items-center justify-center relative">
            <div className="absolute top-4 left-4">
              <Badge className="bg-red-500 text-white animate-pulse">
                <Radio className="w-3 h-3 mr-1" />
                AO VIVO
              </Badge>
            </div>

            {liveEmbedUrl ? (
              <iframe
                src={liveEmbedUrl}
                title="Transmissão ao vivo"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <div className="text-center px-4">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Radio className="w-10 h-10 text-white/60" />
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">
                  Transmissão pelo YouTube
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  Abra o canal para acompanhar o culto ao vivo.
                </p>

                {liveExternalUrl && (
                  <Button
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10"
                    onClick={() => openExternalUrl(liveExternalUrl, { sameWindow: true })}
                  >
                    <Youtube className="w-4 h-4 mr-2" />
                    Abrir transmissão
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                )}
              </div>
            )}

            {liveEmbedUrl && liveExternalUrl && (
              <div className="absolute bottom-3 right-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/30 bg-black/40 text-white hover:bg-black/60"
                  onClick={() => openExternalUrl(liveExternalUrl, { sameWindow: true })}
                >
                  <Youtube className="w-4 h-4 mr-2" />
                  YouTube
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                <Youtube className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h4 className="font-medium text-white">{churchInfo?.name || 'Nossa Igreja'}</h4>
                <p className="text-sm text-gray-400">Canal do YouTube</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Recent Videos */}
        {displayVideos.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Vídeos Recentes</h3>
            <div className="grid grid-cols-2 gap-3">
              {displayVideos.map((video, index) => {
                const thumbnail = video.thumbnail_url || getYouTubeThumbnail(video.youtube_url);
                
                return (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800 rounded-xl overflow-hidden cursor-pointer hover:bg-gray-750 transition-colors"
                    onClick={() => openExternalUrl(video.youtube_url, { sameWindow: true })}
                  >
                    <div className="aspect-video relative">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                          <Play className="w-8 h-8 text-gray-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-5 h-5 text-indigo-600 ml-0.5" />
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="text-white text-sm font-medium line-clamp-2">{video.title}</h4>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-gray-800 rounded-2xl"
        >
          <h4 className="text-white font-medium mb-3">Conecte-se conosco</h4>
          <div className="space-y-2">
            {churchInfo?.youtube_channel && (
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700"
                onClick={() => openExternalUrl(churchInfo.youtube_channel, { sameWindow: true })}
              >
                <Youtube className="w-5 h-5 mr-3 text-red-500" />
                Nosso Canal no YouTube
                <ExternalLink className="w-4 h-4 ml-auto" />
              </Button>
            )}
            {churchInfo?.instagram && (
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700"
                onClick={() => openExternalUrl(churchInfo.instagram, { sameWindow: true })}
              >
                <div className="w-5 h-5 mr-3 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 rounded-lg" />
                Instagram
                <ExternalLink className="w-4 h-4 ml-auto" />
              </Button>
            )}
            {churchInfo?.facebook && (
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700"
                onClick={() => openExternalUrl(churchInfo.facebook, { sameWindow: true })}
              >
                <div className="w-5 h-5 mr-3 bg-blue-600 rounded-lg" />
                Facebook
                <ExternalLink className="w-4 h-4 ml-auto" />
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
